import { useEffect, useRef, useState } from 'react'
import { intervalDomain, linearScale, niceTicks } from '../lib/scale'

export type ChartTone = 'control' | 'variant' | 'significant' | 'negative'

export interface ChartRow {
  label: string
  point: number
  low: number
  high: number
  tone: ChartTone
}

interface Props {
  rows: readonly ChartRow[]
  /** 目盛りの書式 */
  formatTick: (value: number) => string
  /** 0 の位置に基準線を引く（差のチャートで使う） */
  showZeroLine?: boolean | undefined
  caption: string
}

/** これ以上狭くすると目盛りが重なるので、下回る場合は横スクロールに逃がす。 */
const MIN_WIDTH = 480
const LABEL_WIDTH = 116
const ROW_HEIGHT = 34
const AXIS_HEIGHT = 26
const PADDING_TOP = 8
const PADDING_RIGHT = 30

/**
 * 描画領域の幅を実測する。
 *
 * viewBox に width: 100% を掛けて拡大縮小すると、文字も一緒に伸縮して
 * 狭い画面では読めず、広い画面では不釣り合いに大きくなる。実測した幅を
 * そのまま viewBox に使えば倍率が常に 1 になり、文字は指定どおりの大きさの
 * まま図がコンテナを埋める。
 */
function useMeasuredWidth(minWidth: number) {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(minWidth)

  useEffect(() => {
    const element = ref.current
    if (!element || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => {
      const measured = entries[0]?.contentRect.width
      if (measured) setWidth(Math.max(minWidth, Math.round(measured)))
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [minWidth])

  return [ref, width] as const
}

/**
 * 信頼区間を横棒で並べたチャート。
 *
 * 差のチャートでは 0 の基準線が判定そのものを表す。棒が線をまたいでいれば
 * 「差があるとは言えない」ことが、p 値を読まずに見て取れる。
 */
export function IntervalChart({ rows, formatTick, showZeroLine, caption }: Props) {
  const [measureRef, width] = useMeasuredWidth(MIN_WIDTH)

  const domain = intervalDomain(rows, { includeZero: showZeroLine ?? false })
  const plotLeft = LABEL_WIDTH
  const plotRight = width - PADDING_RIGHT
  const scale = linearScale(domain, [plotLeft, plotRight])
  const ticks = niceTicks(domain[0], domain[1], 5)

  const plotHeight = rows.length * ROW_HEIGHT
  const height = PADDING_TOP + plotHeight + AXIS_HEIGHT
  const axisY = PADDING_TOP + plotHeight

  return (
    <div className="chart-wrap" ref={measureRef}>
      <div className="chart">
        {rows.length > 0 && (
          <svg
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            role="img"
            aria-label={caption}
          >
            <title>{caption}</title>

            {ticks.map((tick) => (
              <line
                key={`grid-${tick}`}
                className="chart-axis-line"
                x1={scale(tick)}
                x2={scale(tick)}
                y1={PADDING_TOP}
                y2={axisY}
                opacity={0.35}
              />
            ))}

            {showZeroLine && domain[0] <= 0 && domain[1] >= 0 && (
              <line
                className="chart-zero-line"
                x1={scale(0)}
                x2={scale(0)}
                y1={PADDING_TOP - 4}
                y2={axisY}
              />
            )}

            <line
              className="chart-axis-line"
              x1={plotLeft}
              x2={plotRight}
              y1={axisY}
              y2={axisY}
            />

            {ticks.map((tick) => (
              <text
                key={`tick-${tick}`}
                className="chart-tick-label"
                x={scale(tick)}
                y={axisY + 15}
                textAnchor="middle"
              >
                {formatTick(tick)}
              </text>
            ))}

            {rows.map((row, index) => {
              const y = PADDING_TOP + index * ROW_HEIGHT + ROW_HEIGHT / 2
              const x1 = scale(row.low)
              const x2 = scale(row.high)
              // 片側検定では区間の端が無限になる。描画域の端で切り、
              // 閉じていないことを示すため端のキャップを描かない。
              const lowOpen = !Number.isFinite(row.low)
              const highOpen = !Number.isFinite(row.high)
              return (
                <g key={row.label}>
                  <text
                    className="chart-row-label"
                    x={LABEL_WIDTH - 12}
                    y={y + 4}
                    textAnchor="end"
                  >
                    {row.label}
                  </text>
                  <line
                    className={`chart-bar chart-bar-${row.tone}`}
                    x1={x1}
                    x2={x2}
                    y1={y}
                    y2={y}
                  />
                  {!lowOpen && (
                    <line
                      className={`chart-cap chart-cap-${row.tone}`}
                      x1={x1}
                      x2={x1}
                      y1={y - 5}
                      y2={y + 5}
                    />
                  )}
                  {!highOpen && (
                    <line
                      className={`chart-cap chart-cap-${row.tone}`}
                      x1={x2}
                      x2={x2}
                      y1={y - 5}
                      y2={y + 5}
                    />
                  )}
                  <circle
                    className={`chart-point chart-point-${row.tone}`}
                    cx={scale(row.point)}
                    cy={y}
                    r={4}
                  />
                </g>
              )
            })}
          </svg>
        )}
      </div>
      <p className="chart-hint">← 横にスクロールすると全体が見えます</p>
    </div>
  )
}
